import { useMatchSmallViewport } from '@avensia/excite';
import { Image } from '@avensia/excite-ui';
import { styled } from '@glitz/react';
import { theme } from 'nordicnest/shared/theme';
import * as React from 'react';
import { Carousel, useCarousel, type DeviceSettingType } from 'webapp/components/Carousel';
import { SquareImage } from 'webapp/components/image';
import { Video } from 'webapp/components/video/Video';
import { VideoPlaceholder } from 'webapp/components/video/VideoPlaceholder';
import { isVideo } from 'webapp/components/video/checkForVideo';
import { createImageAspectRatioDecorator } from 'webapp/decorators/aspect-ratio';
import Badge from 'webapp/product/Badge';
import { linkTitle } from 'webapp/product/product-seo-info';
import { DESKTOP_HEADER_HEIGHT } from 'webapp/utils/constants';
import { useCalcPrice } from 'webapp/variant-selectors/use-bundle-selectors';
import { createMaxMobileViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import ProductImageFullscreenGallery from './ProductImageFullscreenGallery';
import { useProductImagesGalleryContext } from './ProductImages';
import { applyImageBackground } from './helpers';
import useProduct3DModel from './useProduct3DModel';

const GALLER_MAX_HEIGHT: number = 800;

type PropType = {
  product: NordicNest.Api.PartialProductApiModel;
  packageKey?: string;
};

const visibleCount: DeviceSettingType = { mobile: 1, tablet: 1, desktop: 1 };

function seoInfo(displayName: string, brandName: string) {
  return brandName ? `${displayName} ${brandName}` : displayName;
}

export default function ProductImageCarousel(props: PropType) {
  const isSmallViewport = useMatchSmallViewport();
  const { price } = useCalcPrice(props.product.price, props.packageKey);
  const { product3dModelAvailable, product3dModelThumbnail, product3dModelComponent } = useProduct3DModel(
    props.product.sku,
    props.product.source3dModel,
  );

  const { setActiveSlide: setActiveSlideGallery, setIsOpen } = useProductImagesGalleryContext();

  const thumbnails = useCarousel(undefined, {
    axis: isSmallViewport ? 'x' : 'y',
    slidesToScroll: 1,
    dragFree: true,
  });
  const thumbnailImages = product3dModelAvailable
    ? [product3dModelThumbnail, ...props.product.images]
    : props.product.images;

  const largeViewCarousel = useCarousel(undefined, {
    watchDrag: product3dModelAvailable ? emblaApi => emblaApi.selectedScrollSnap() !== 0 : true,
  });

  const openGallery = React.useCallback(
    (index: number) => {
      setActiveSlideGallery(index);
      setIsOpen(true);
    },
    [setActiveSlideGallery, setIsOpen],
  );

  const slideTo = React.useCallback(
    (index: number) => {
      largeViewCarousel.emblaApi?.scrollTo(index);
    },
    [largeViewCarousel.emblaApi],
  );
  const singleCenterSlideDecorator = styled(
    createMinLargeViewportDecorator({ ...(thumbnailImages?.length < 2 && { justifyContent: 'center' }) }),
  );

  largeViewCarousel.emblaApi?.on(
    'select',
    () =>
      largeViewCarousel?.emblaApi && thumbnails.emblaApi?.scrollTo(largeViewCarousel.emblaApi?.selectedScrollSnap()),
  );

  return (
    <>
      <GridLayout>
        <LargeView>
          <LargeViewCarousel
            arrowNavigation
            visibleCount={visibleCount}
            useTotalCount
            arrowPosition={isSmallViewport ? theme.sharedSpacing.m : theme.sharedSpacing.l}
            gap={isSmallViewport ? 0 : theme.sharedSpacing.xxl}
            css={singleCenterSlideDecorator}
            containerDecorator={largeViewDecorator}
            {...largeViewCarousel}
          >
            {product3dModelAvailable && product3dModelComponent}
            {props.product.images.map((img, index) => (
              <ImageWrapper key={index}>
                {isVideo(img.url) ? (
                  <VideoEl url={img.url} />
                ) : (
                  <ImageEl
                    tabIndex={0}
                    onClick={() => openGallery(index)}
                    title={seoInfo(props.product.displayName, props.product.brand)}
                    alt={seoInfo(props.product.displayName, props.product.brand)}
                    url={img.url}
                    sizes={'40vw'}
                    preset={isSmallViewport ? 'tiny' : 'medium'}
                    loading={index > 0 ? 'lazy' : undefined}
                    css={!isSmallViewport && createImageAspectRatioDecorator(img)}
                  />
                )}
              </ImageWrapper>
            ))}
          </LargeViewCarousel>
          <Badge
            newsBadge={props.product.newsBadge}
            badge={props.product.price.discountBadge}
            discount={props.product.price.discount}
            align={'left'}
            priceModel={price}
            isBundle={props.product.isPackage}
          />
        </LargeView>
        {thumbnailImages.length > 1 ? (
          <ThumbnailViewWrapper>
            <ThumbnailCarousel
              useTotalCount
              gap={isSmallViewport ? theme.spacing.xs : theme.spacing.s}
              progressDecorator={progressBarDecorator}
              progressBar={isSmallViewport ? props.product.images.length > 4 : props.product.images.length > 6}
              containerDecorator={thumbnailsContainerDecorator}
              {...thumbnails}
            >
              {thumbnailImages.map((img, index) => (
                <ThumbnailWrapper
                  key={index}
                  onClick={() => slideTo(index)}
                  css={styled(index === largeViewCarousel.currentSlide && activeSlideDecorator)}
                >
                  {img ? (
                    isVideo(img.url) ? (
                      <VideoPlaceholder css={videoIconDecorator} />
                    ) : (
                      <Thumbnail
                        url={img.url}
                        alt={linkTitle(props.product)}
                      preset={'thumb'}
                        css={createImageAspectRatioDecorator(img)}
                      />
                    )
                  ) : (
                    <ThumbnailPlaceholder />
                  )}
                </ThumbnailWrapper>
              ))}
            </ThumbnailCarousel>
          </ThumbnailViewWrapper>
        ) : null}
      </GridLayout>
      <ProductImageFullscreenGallery images={props.product.images} />
    </>
  );
}

const LargeView = styled.div({
  gridArea: 'largeview',
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
  height: '100%',
});

const largeViewDecorator = styled({
  position: 'relative',
  height: '100%',
});

const videoIconDecorator = styled(
  createMinLargeViewportDecorator({
    width: 106,
    height: 'auto',
    maxHeight: t => `calc(100svh - ${t.sharedSpacing.m + 204}px)`,
  }),
  {
    width: 96,
    height: 76,
    objectFit: 'contain',
  },
);

const progressBarDecorator = styled(
  createMinLargeViewportDecorator({
    margin: { xy: 0 },
    padding: { xy: 0 },
    height: '100%',
    position: 'absolute',
    top: 0,
    left: t => `-${t.sharedSpacing.m}px`,
  }),
  createMaxMobileViewportDecorator({
    margin: { right: t => t.sharedSpacing.m },
  }),
  {
    margin: { left: t => t.sharedSpacing.m },
  },
);

const thumbnailsContainerDecorator = styled(
  createMinLargeViewportDecorator({
    flexDirection: 'column',
    margin: { x: 0 },
    maxHeight: GALLER_MAX_HEIGHT,
    width: 'auto',
  }),
  createMaxMobileViewportDecorator({
    margin: { right: t => t.sharedSpacing.m },
  }),
  {
    margin: { left: t => t.sharedSpacing.m },
    display: 'flex',
  },
);

const ThumbnailViewWrapper = styled.div(
  {
    display: 'flex',
    gridArea: 'thumbnails',
    maxHeight: GALLER_MAX_HEIGHT,
    flexDirection: 'column',
  },
  createMinLargeViewportDecorator({
    flexDirection: 'row',
  }),
);

const LargeViewCarousel = styled(
  Carousel,
  createMinLargeViewportDecorator({
    paddingBottom: 0,
  }),
  {
    position: 'relative',
  },
);

const ThumbnailWrapper = styled.div(
  {
    position: 'relative',
    border: { xy: { width: 2, color: t => t.thumbnailBorder, style: 'solid' } },
    flexShrink: 0,
    flexGrow: 0,
    cursor: 'pointer',
  },
  applyImageBackground && {
    backgroundColor: t => t.imageBackgroundColor,
  },
);

const ThumbnailCarousel = styled(
  Carousel,
  createMinLargeViewportDecorator({
    margin: { x: 0 },
    maxHeight: GALLER_MAX_HEIGHT,
  }),
  {
    display: 'flex',
    height: 'auto',
  },
);

const GridLayout = styled.div(
  createMinLargeViewportDecorator({
    gridTemplateAreas: '"thumbnails largeview"',
    gridTemplateColumns: 'minmax(min-content, auto) 1fr',
    gridTemplateRows: 'auto',
    gap: t => t.spacing.l,
    width: '100%',
    maxHeight: GALLER_MAX_HEIGHT,
    position: 'sticky',
    top: t => `calc(${DESKTOP_HEADER_HEIGHT}px + ${t.sharedSpacing.m}px)`,
  }),
  {
    display: 'grid',
    gridTemplateRows: '1fr minmax(min-content, auto)',
    gridTemplateAreas: '"largeview" "thumbnails"',
    position: 'relative',
    gap: t => t.sharedSpacing.m,
  },
);

const ImageWrapper = styled.div(
  {
    display: 'flex',
    alignItems: 'center',
  },
  applyImageBackground && {
    backgroundColor: t => t.imageBackgroundColor,
  },
);

const ImageEl = styled(
  Image,
  createMinLargeViewportDecorator({
    maxHeight: GALLER_MAX_HEIGHT,
  }),
  {
    objectFit: 'contain',
    width: '100%',
    cursor: 'pointer',
  },
  createMaxMobileViewportDecorator({
    aspectRatio: '1 / 1',
  }),
  applyImageBackground && { mixBlendMode: 'multiply' },
);

const VideoEl = styled(Video, {
  aspectRatio: '1 / 1',
});

const Thumbnail = styled(
  Image,
  createMinLargeViewportDecorator({
    width: 106,
    height: 'auto',
    maxHeight: t => `calc(100svh - ${t.sharedSpacing.m + 204}px)`,
  }),
  {
    width: 'auto',
    height: 76,
    objectFit: 'contain',
  },
  applyImageBackground && {
    mixBlendMode: 'multiply',
  },
);

const ThumbnailPlaceholder = styled(
  SquareImage,
  createMinLargeViewportDecorator({
    width: 'auto',
    height: 106,
  }),
  { width: 76, height: 'auto', objectFit: 'contain' },
);

const activeSlideDecorator = styled({
  border: { xy: { color: t => t.borderHighlight } },
});
